import { watch, toRaw, type ComputedRef } from 'vue'
import { QueryObserver, type QueryClient } from '@tanstack/vue-query'
import { criarOpcoesConsultaCatalogo, hidratarCatalogoPersistido } from '@next/compartilhados/catalogos/catalogo.query'
import { criarQueryKeyCatalogo } from '@next/compartilhados/catalogos/catalogo.chave'
import { obterSincronizacaoCatalogo } from '@next/compartilhados/catalogos/catalogo.sincronizacao'
import type { CatalogoDescriptor, CatalogosPersistidos, ContextoDeCatalogo } from '@next/compartilhados/catalogos/catalogo.tipos'
import { FalhaHttp } from '@next/infraestrutura/http'

type StoreCatalogos = { commit: (tipo: string, valor?: unknown) => void }
export type DependenciasCatalogoVuex<T> = Readonly<{
  catalogo: CatalogoDescriptor<T>
  modulo: string
  store: StoreCatalogos
  queryClient: QueryClient
  catalogosPersistidos: CatalogosPersistidos
  contexto: ComputedRef<ContextoDeCatalogo | null>
  consultar: (sinal: AbortSignal) => Promise<readonly T[]>
}>
const cargas = new WeakMap<StoreCatalogos, Map<string, (recarregar: boolean) => Promise<void>>>()

export const carregarCatalogoDaStore = (store: StoreCatalogos, modulo: string, recarregar = false): Promise<void> => {
  const carregar = cargas.get(store)?.get(modulo)
  if (!carregar) return Promise.reject(new Error(`Integracao do catalogo ${modulo} nao instalada`))
  return carregar(recarregar)
}

export const instalarCatalogoVuex = <T>({
  store, queryClient, catalogosPersistidos, contexto, consultar, catalogo: CATALOGO, modulo,
}: DependenciasCatalogoVuex<T>): (() => void) => {
  let chaveAtual: string | null = null
  let hidratando = false
  let ultimoSnapshot: unknown
  let ultimoErroDeAcesso: unknown
  let geracao = 0
  let pararRetencao: (() => void) | undefined

  const publicar = () => {
    const atual = contexto.value
    if (!atual || chaveAtual !== JSON.stringify(criarQueryKeyCatalogo(CATALOGO.nome, atual))) return
    const estado = queryClient.getQueryState<readonly T[]>(criarQueryKeyCatalogo(CATALOGO.nome, atual))
    const erro = estado?.error ?? null
    const semAcesso = erro instanceof FalhaHttp && (erro.status === 401 || erro.status === 403)
    const pronto = !semAcesso && estado?.data !== undefined
    const snapshot = pronto ? estado?.data : undefined
    const items = snapshot !== ultimoSnapshot
      ? { items: snapshot ? structuredClone(toRaw(snapshot)) : [] }
      : {}
    ultimoSnapshot = snapshot
    store.commit(`${modulo}/publicarCatalogo`, {
      ...items, pronto, erro,
      sincronizadoEm: pronto ? obterSincronizacaoCatalogo(snapshot) : null,
      loading: !pronto && !erro && (hidratando || estado?.fetchStatus === 'fetching'),
      atualizando: estado?.fetchStatus === 'fetching',
    })
    if (semAcesso && erro !== ultimoErroDeAcesso) {
      ultimoErroDeAcesso = erro
      void catalogosPersistidos.invalidar(CATALOGO, atual)
    }
  }

  const carregar = async (recarregar: boolean) => {
    const atual = contexto.value
    if (!atual) return
    const revisao = geracao
    if (recarregar) {
      await queryClient.cancelQueries({ queryKey: criarQueryKeyCatalogo(CATALOGO.nome, atual) })
      await catalogosPersistidos.invalidar(CATALOGO, atual)
      await queryClient.invalidateQueries({ queryKey: criarQueryKeyCatalogo(CATALOGO.nome, atual), refetchType: 'none' })
    }
    try {
      await hidratarCatalogoPersistido(queryClient, catalogosPersistidos, CATALOGO, atual)
      if (revisao !== geracao) throw new Error(`Contexto de ${modulo} alterado durante a carga`)
      const estado = queryClient.getQueryState(criarQueryKeyCatalogo(CATALOGO.nome, atual))
      const semAcesso = estado?.error instanceof FalhaHttp && (estado.error.status === 401 || estado.error.status === 403)
      const carga = queryClient.fetchQuery(criarOpcoesConsultaCatalogo(catalogosPersistidos, CATALOGO, atual, consultar))
      if (!recarregar && estado?.data !== undefined && !semAcesso) {
        void carga.catch(() => undefined)
        publicar()
        return
      }
      await carga
    } catch (erro) {
      if (revisao === geracao) publicar()
      throw erro
    }
    if (revisao !== geracao) throw new Error(`Contexto de ${modulo} alterado durante a carga`)
    publicar()
  }
  const cargasDaStore = cargas.get(store) ?? new Map()
  cargas.set(store, cargasDaStore)
  cargasDaStore.set(modulo, carregar)
  const pararCache = queryClient.getQueryCache().subscribe(evento => {
    if (JSON.stringify(evento.query.queryKey) === chaveAtual) publicar()
  })
  const pararContexto = watch(() => contexto.value
    ? JSON.stringify(criarQueryKeyCatalogo(CATALOGO.nome, contexto.value)) : null,
  chave => {
    const revisao = ++geracao
    pararRetencao?.()
    pararRetencao = undefined
    if (chaveAtual) {
      void queryClient.cancelQueries({ queryKey: JSON.parse(chaveAtual) as readonly string[] })
    }
    chaveAtual = chave
    ultimoSnapshot = undefined
    ultimoErroDeAcesso = undefined
    store.commit(`${modulo}/resetItems`)
    const atual = contexto.value
    if (!atual) return
    hidratando = true
    // O Vuex mantém o catálogo em uso mesmo sem seletores montados; a carga continua explícita.
    const observador = new QueryObserver(queryClient, {
      ...criarOpcoesConsultaCatalogo(catalogosPersistidos, CATALOGO, atual, consultar),
      enabled: false,
    })
    pararRetencao = observador.subscribe(() => undefined)
    publicar()
    void (async () => {
      try {
        await hidratarCatalogoPersistido(queryClient, catalogosPersistidos, CATALOGO, atual)
        if (revisao !== geracao) return
        hidratando = false
        publicar()
        await queryClient.fetchQuery(criarOpcoesConsultaCatalogo(catalogosPersistidos, CATALOGO, atual, consultar))
      } finally {
        if (revisao === geracao) { hidratando = false; publicar() }
      }
    })().catch(() => undefined)
  }, { immediate: true, flush: 'sync' })

  return () => { geracao++; pararContexto(); pararRetencao?.(); pararCache(); cargasDaStore.delete(modulo) }
}
